/**
 * 파일 드롭존 — 끌어다 놓거나 눌러서 고른다.
 *
 * <p>첨부 UI 가 화면마다 «버튼 + 숨은 input» 으로 따로 있어서, 드래그를 원하는 화면이 생길 때마다
 * 같은 코드를 다시 쓰게 된다. 그 부분만 여기로 모은다. 파일을 <b>어디에 담을지</b>는 호출부가
 * 그대로 들고 있으므로({@code onFilesAdded}) 기존 첨부 목록·검증 로직을 건드리지 않는다.
 *
 * <h3>드롭 판정 — types 에 «Files» 가 있을 때만</h3>
 * <p>드래그되는 것이 파일이 아닐 수 있다. 브라우저에서 글자를 긁어 끌면 {@code text/plain} 이고,
 * 이 앱 안에는 순서 바꾸기용 드래그도 여럿 있다(결재선·계좌·메일 폴더). {@code dataTransfer.types}
 * 에 «Files» 가 있는지 먼저 보지 않으면 그런 드래그에도 드롭존이 켜져 화면이 번쩍인다.
 *
 * <h3>dragleave 를 세는 이유</h3>
 * <p>자식 요소 위로 커서가 옮겨갈 때마다 dragleave 가 뜬다. 그때마다 끄면 드롭존 안에서 커서를
 * 움직이는 동안 테두리가 깜빡인다. enter/leave 를 세어 0 이 될 때만 끈다.
 *
 * <h3>두 가지 모양</h3>
 * <ul>
 *   <li><b>상자</b>(기본) — 점선 상자를 그린다. 첨부 자리가 따로 있는 화면용.</li>
 *   <li><b>감싸기</b>({@code children} 을 주면) — 넘긴 내용을 그대로 그리고, 파일을 끌고 오는
 *       동안에만 테두리와 «여기에 놓으세요» 를 덧씌운다. 피드 하단 한 줄짜리 작성칸처럼
 *       상시 점선 상자를 둘 자리가 없는 곳용이다. 클릭으로 파일 고르기는 켜지 않는다 —
 *       그 안에 이미 버튼·입력칸이 있어서 아무 데나 눌러도 창이 뜨면 방해가 된다.</li>
 * </ul>
 *
 * @param onFilesAdded 고른/떨어뜨린 파일 배열. 검증(용량·개수·확장자)은 호출부 몫이다.
 * @param accept       input 의 accept. 드롭에는 브라우저가 적용하지 않으므로 호출부가 걸러야 한다.
 * @param label        평소 문구
 * @param hint         label 아래 작은 설명
 * @param children     주면 «감싸기» 모양이 된다.
 */
import React, { useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";

export default function FileDropzone({
    onFilesAdded,
    accept,
    multiple = true,
    disabled = false,
    label = "파일을 끌어다 놓거나 눌러서 선택",
    hint,
    sx,
    children,
}) {
    const wrap = Boolean(children);
    const inputRef = useRef(null);
    const depth    = useRef(0);          // dragenter/leave 카운터 — 자식 위를 지날 때 꺼지지 않게
    const [over, setOver] = useState(false);

    const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");

    const emit = (list) => {
        const files = Array.from(list || []);
        if (files.length > 0) onFilesAdded?.(files);
    };

    const onDragEnter = (e) => {
        if (disabled || !hasFiles(e)) return;
        e.preventDefault();
        depth.current += 1;
        setOver(true);
    };

    const onDragOver = (e) => {
        if (disabled || !hasFiles(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
    };

    const onDragLeave = (e) => {
        if (disabled) return;
        e.preventDefault();
        depth.current -= 1;
        if (depth.current <= 0) { depth.current = 0; setOver(false); }
    };

    const onDrop = (e) => {
        if (disabled) return;
        e.preventDefault();
        depth.current = 0;
        setOver(false);
        emit(e.dataTransfer?.files);
    };

    const fileInput = (
        <input
            ref={inputRef}
            type="file"
            hidden
            multiple={multiple}
            accept={accept}
            onChange={(e) => {
                emit(e.target.files);
                // 같은 파일을 연속으로 고를 수 있게 초기화 — 안 하면 두 번째 선택에서 change 가 안 뜬다.
                e.target.value = "";
            }}
        />
    );

    if (wrap) {
        return (
            <Box
                onDragEnter={onDragEnter}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                sx={{ position: "relative", borderRadius: 1.5, ...sx }}
            >
                {children}
                {over && (
                    <Box sx={{
                        position: "absolute", inset: 0, zIndex: 3,
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 0.6,
                        border: "1.5px dashed #2563eb", borderRadius: 1.5,
                        bgcolor: "rgba(37,99,235,0.08)", color: "#1d4ed8",
                        pointerEvents: "none",   // 덮개가 드롭을 가로채면 안 된다
                    }}>
                        <CloudUploadRoundedIcon sx={{ fontSize: 20 }} />
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>여기에 놓으세요</Typography>
                    </Box>
                )}
                {fileInput}
            </Box>
        );
    }

    return (
        <Box
            onDragEnter={onDragEnter}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => { if (!disabled) inputRef.current?.click(); }}
            sx={{
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                gap: 0.4, py: 1.6, px: 1.5,
                border: "1.5px dashed", borderRadius: 1.5,
                borderColor: over ? "#2563eb" : "#cbd5e1",
                bgcolor: over ? "rgba(37,99,235,0.06)" : "#f8fafc",
                color: over ? "#1d4ed8" : "#64748b",
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.6 : 1,
                transition: "background-color .12s, border-color .12s",
                "&:hover": disabled ? undefined : { borderColor: "#94a3b8", bgcolor: "#f1f5f9" },
                ...sx,
            }}
        >
            <CloudUploadRoundedIcon sx={{ fontSize: 20 }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
                {over ? "여기에 놓으세요" : label}
            </Typography>
            {hint && !over && (
                <Typography sx={{ fontSize: 11, color: "#94a3b8", textAlign: "center" }}>
                    {hint}
                </Typography>
            )}

            {fileInput}
        </Box>
    );
}
